/**
 * Diff helpers. Pure and DOM-free.
 *
 * The UI supports two diff modes:
 *  - "raw": diff the exact input text (no transformation). Nothing is hidden.
 *  - "semantic": parse both sides, recursively sort object keys, and
 *    pretty-print so that key-ordering and whitespace differences disappear and
 *    only real value/shape differences remain.
 *
 * Semantic mode intentionally normalizes; callers should surface a warning that
 * duplicate keys and number/string representation may be affected (see
 * {@link validate}). Scalar tokens are still preserved verbatim here because we
 * re-serialize from the lossless AST rather than via JSON.stringify.
 */

import {
  parse,
  scalarEqual,
  nodePreview,
  pathKeySegment,
  type JsonNode,
  type ObjectNode,
} from './json';

const INDENT = '  ';

function sortNode(node: JsonNode): JsonNode {
  switch (node.type) {
    case 'object': {
      const members = node.members
        .map((m) => ({ ...m, value: sortNode(m.value) }))
        .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
      return { ...node, members } as ObjectNode;
    }
    case 'array':
      return { ...node, elements: node.elements.map(sortNode) };
    default:
      return node;
  }
}

function serializeSorted(node: JsonNode, level: number): string {
  switch (node.type) {
    case 'string':
    case 'number':
    case 'literal':
      return node.raw;
    case 'array': {
      if (node.elements.length === 0) return '[]';
      const inner = INDENT.repeat(level + 1);
      const items = node.elements.map(
        (el) => inner + serializeSorted(el, level + 1),
      );
      return `[\n${items.join(',\n')}\n${INDENT.repeat(level)}]`;
    }
    case 'object': {
      if (node.members.length === 0) return '{}';
      const inner = INDENT.repeat(level + 1);
      const items = node.members.map(
        (m) => inner + m.keyRaw + ': ' + serializeSorted(m.value, level + 1),
      );
      return `{\n${items.join(',\n')}\n${INDENT.repeat(level)}}`;
    }
  }
}

/**
 * Produce a canonical string for semantic diffing: keys sorted recursively,
 * pretty-printed with 2-space indent. Throws {@link JsonSyntaxError} if the
 * text is not valid JSON.
 */
export function normalizeForSemanticDiff(text: string): string {
  return serializeSorted(sortNode(parse(text)), 0);
}

// --- Structural diff --------------------------------------------------------

export type DiffKind = 'added' | 'removed' | 'changed';

export interface DiffEntry {
  kind: DiffKind;
  /** JSONPath-like location, e.g. `$.a.b[0]`. */
  path: string;
  /** Bounded preview of the left value (present for removed/changed). */
  left?: string;
  /** Bounded preview of the right value (present for added/changed). */
  right?: string;
}

export interface StructuralDiffResult {
  entries: DiffEntry[];
  added: number;
  removed: number;
  changed: number;
  /** True if the entry cap was hit and some differences were omitted. */
  truncated: boolean;
  /** True if duplicate object keys were collapsed (last value wins). */
  duplicateKeys: boolean;
}

/** Beyond this many entries we stop, to keep the summary and UI bounded. */
const MAX_ENTRIES = 500;
/** Guards against pathologically deep structures overflowing the stack. */
const MAX_DEPTH = 1000;

export class DiffLimitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DiffLimitError';
  }
}

interface DiffCtx {
  entries: DiffEntry[];
  duplicateKeys: boolean;
  truncated: boolean;
}

function lastValueMap(obj: ObjectNode, ctx: DiffCtx): Map<string, JsonNode> {
  const map = new Map<string, JsonNode>();
  for (const m of obj.members) {
    if (map.has(m.key)) ctx.duplicateKeys = true;
    map.set(m.key, m.value);
  }
  return map;
}

function emit(ctx: DiffCtx, entry: DiffEntry): boolean {
  if (ctx.entries.length >= MAX_ENTRIES) {
    ctx.truncated = true;
    return false;
  }
  ctx.entries.push(entry);
  return true;
}

function diffNodes(
  a: JsonNode,
  b: JsonNode,
  path: string,
  depth: number,
  ctx: DiffCtx,
): void {
  if (ctx.truncated) return;
  if (depth > MAX_DEPTH) {
    throw new DiffLimitError('Structure is too deeply nested to diff safely.');
  }

  if (a.type !== b.type) {
    emit(ctx, { kind: 'changed', path, left: nodePreview(a), right: nodePreview(b) });
    return;
  }

  if (a.type === 'object' && b.type === 'object') {
    const am = lastValueMap(a, ctx);
    const bm = lastValueMap(b, ctx);
    const seen = new Set<string>();
    for (const key of am.keys()) {
      seen.add(key);
      const childPath = path + pathKeySegment(key);
      const bv = bm.get(key);
      if (bv === undefined) {
        if (!emit(ctx, { kind: 'removed', path: childPath, left: nodePreview(am.get(key)!) })) return;
      } else {
        diffNodes(am.get(key)!, bv, childPath, depth + 1, ctx);
        if (ctx.truncated) return;
      }
    }
    for (const key of bm.keys()) {
      if (seen.has(key)) continue;
      const childPath = path + pathKeySegment(key);
      if (!emit(ctx, { kind: 'added', path: childPath, right: nodePreview(bm.get(key)!) })) return;
    }
    return;
  }

  if (a.type === 'array' && b.type === 'array') {
    const len = Math.max(a.elements.length, b.elements.length);
    for (let i = 0; i < len; i++) {
      const childPath = `${path}[${i}]`;
      const av = a.elements[i];
      const bv = b.elements[i];
      if (av === undefined) {
        if (!emit(ctx, { kind: 'added', path: childPath, right: nodePreview(bv!) })) return;
      } else if (bv === undefined) {
        if (!emit(ctx, { kind: 'removed', path: childPath, left: nodePreview(av) })) return;
      } else {
        diffNodes(av, bv, childPath, depth + 1, ctx);
        if (ctx.truncated) return;
      }
    }
    return;
  }

  // Both scalars of the same node type.
  if (!scalarEqual(a, b)) {
    emit(ctx, { kind: 'changed', path, left: nodePreview(a), right: nodePreview(b) });
  }
}

/**
 * Compute a structural (path-based) diff of two JSON documents. Arrays are
 * compared by index (documented in the UI). Throws {@link JsonSyntaxError} if
 * either side is invalid, or {@link DiffLimitError} for pathological depth.
 */
export function structuralDiff(
  leftText: string,
  rightText: string,
): StructuralDiffResult {
  let a: JsonNode;
  let b: JsonNode;
  try {
    a = parse(leftText);
    b = parse(rightText);
  } catch (e) {
    // The parser is recursive, so extremely deep (but otherwise valid) input can
    // overflow the stack before diffNodes' own depth guard fires. Surface that as
    // a friendly DiffLimitError rather than a raw RangeError.
    if (e instanceof RangeError) {
      throw new DiffLimitError('Structure is too deeply nested to diff safely.');
    }
    throw e;
  }
  const ctx: DiffCtx = { entries: [], duplicateKeys: false, truncated: false };
  diffNodes(a, b, '$', 0, ctx);
  let added = 0;
  let removed = 0;
  let changed = 0;
  for (const e of ctx.entries) {
    if (e.kind === 'added') added++;
    else if (e.kind === 'removed') removed++;
    else changed++;
  }
  return {
    entries: ctx.entries,
    added,
    removed,
    changed,
    truncated: ctx.truncated,
    duplicateKeys: ctx.duplicateKeys,
  };
}
